import { useState } from "react";
import { useQuery } from "@apollo/client";
import { NSP_CORE_INFO_BY_TYPE } from "graphql/invoices.gql";
import { VendorInvoice } from "types/invoices";
import { generateDraftInvoicePDF, generateVendorInvoicePDF, generateNspInvoicePDF } from "@utils/invoice-generator";
import { useInvoiceDownloadAudit } from "@hooks/graphql/useInvoiceDownloadAudit";

interface UseVendorInvoiceParams {
  vendor: VendorInvoice;
  monthYear: string;
}

// Helper to format numbers
const fmtWholeNumber = (v: number | string | null | undefined) =>
  Math.round(Number(v ?? 0)).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const fmtCurrency = (v: number | null | undefined) =>
  (v ?? 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });


const useVendorInvoice = ({ vendor, monthYear }: UseVendorInvoiceParams) => {
  const [expanded, setExpanded] = useState(false);

  const { data: primaryIssuerData } = useQuery(NSP_CORE_INFO_BY_TYPE, {
    variables: { entryType: "primary" },
    fetchPolicy: "cache-first",
  });

  const primaryIssuer = primaryIssuerData?.nspCoreInfoByType ?? null;
  const hstRate = primaryIssuer?.hstRate ?? 0;

  const { logNsp, logVendor, logPreview } = useInvoiceDownloadAudit();

  const handleDownloadPDF = async () => {
    await generateDraftInvoicePDF({
      vendor,
      primaryIssuer,
      monthYear,
      hstRate,
      applyHst: true,
    });
    logPreview({ ippId: vendor.vendor_id!, monthYear, vendorName: vendor.vendor_name });
  };

  const handleDownloadVendorPDF = async () => {
    await generateVendorInvoicePDF({
      vendor,
      primaryIssuer,
      monthYear,
      hstRate,
      applyHst: true,
    });
    logVendor({ ippId: vendor.vendor_id!, monthYear, vendorName: vendor.vendor_name });
  };

  const handleDownloadNspPDF = async () => {
    await generateNspInvoicePDF({
      vendor,
      primaryIssuer,
      monthYear,
      hstRate,
      applyHst: true,
    });
    logNsp({ ippId: vendor.vendor_id!, monthYear, vendorName: vendor.vendor_name });
  };

  return {
    expanded,
    setExpanded,
    hstRate,
    fmtWholeNumber,
    fmtCurrency,
    handleDownloadPDF,
    handleDownloadVendorPDF,
    handleDownloadNspPDF,
  };
};

export default useVendorInvoice;
